import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useLanguage } from '../../hooks/useLanguage';
import { useAuth } from '../../hooks/useAuth';
import { userService } from '../../services/AppDataServices/services/user.service';
import ActivitySettingsService from '../../services/AppDataServices/services/ActivitySettings.service';
import { SettingsService } from '../../services/AppDataServices/services/settings.service';
import useMyAppStore from '../../stores/myAppStore';

const LoginPage = () => {
  const { lang } = useLanguage();
  const navigate = useNavigate();
  const { login } = useAuth();
  const { setCurrentActivity, setCurrentUser, setActivitySettings } = useMyAppStore();
  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    
    try {
      console.log('🔐 Début de l\'authentification...');
      
      // 1. Authentifier l'utilisateur avec le service
      const user = await userService.authenticate(formData.username, formData.password);
      console.log('✅ Utilisateur authentifié:', user);

      // 2. Définir l'utilisateur dans le store global
      const userData = {
        id: user.id,
        username: user.username,
        firstName: user.firstName || user.username,
        lastName: user.lastName || '',
        email: user.email || '',
        role: user.role || 'user'
      };
      
      setCurrentUser(userData);
      console.log('👤 Utilisateur mis à jour dans le store');

      // 3. Connexion réussie (maintenant currentUser existe dans le store)
      await login(userData);
      console.log('🔑 Session créée');

      // 4. Charger les paramètres d'activité depuis la base de données
      console.log('📥 Chargement des paramètres d\'activité...');
      try {
        const activitySettings = await ActivitySettingsService.getActivitySettings();
        if (activitySettings) {
          setActivitySettings(activitySettings);
          console.log('⚙️ Paramètres d\'activité chargés:', activitySettings);
        } else {
          console.log('⚠️ Aucun paramètre d\'activité trouvé');
        }
      } catch (activityError) {
        console.warn('⚠️ Erreur lors du chargement des paramètres d\'activité:', activityError);
      }

      // 5. Charger les paramètres système depuis la base de données
      console.log('📥 Chargement des paramètres système...');
      try {
        const settingsService = new SettingsService();
        const systemSettings = await settingsService.getSystemSettings();
        const userSettings = await settingsService.getUserSettings();
        
        console.log('⚙️ Paramètres système chargés:', systemSettings);
        console.log('⚙️ Paramètres utilisateur chargés:', userSettings);
        
        // Optionnel : Stocker les settings dans le store si nécessaire
        // setSystemSettings(systemSettings);
        // setUserSettings(userSettings);
      } catch (settingsError) {
        console.warn('⚠️ Erreur lors du chargement des paramètres système:', settingsError);
      }

      // 6. Charger l'activité actuelle si elle existe dans les settings
      console.log('📥 Chargement de l\'activité actuelle...');
      try {
        const settingsService = new SettingsService();
        const currentActivitySetting = await settingsService.getSettingByKey('current_activity');
        if (currentActivitySetting) {
          const activityInfo = {
            id: currentActivitySetting.value,
            name: currentActivitySetting.value, // Ou récupérer le nom depuis une autre source
            lastUpdated: new Date(currentActivitySetting.updatedAt)
          };
          setCurrentActivity(activityInfo);
          console.log('🏢 Activité actuelle chargée:', activityInfo);
        } else {
          console.log('⚠️ Aucune activité actuelle trouvée');
        }
      } catch (activityError) {
        console.warn('⚠️ Erreur lors du chargement de l\'activité actuelle:', activityError);
      }

      console.log('🎉 Connexion et chargement des données terminés avec succès');
      
      // 7. Redirection directe vers l'accès rapide
      navigate(`/${lang}/myapp/quick-access`);
    } catch (err) {
      console.error('❌ Erreur de connexion:', err);
      setError(err.message || 'Identifiants incorrects. Veuillez réessayer.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleShowStore = () => {
    const storeState = useMyAppStore.getState();
    console.log('📦 État complet du store myAppStore:', storeState);
    console.log('👤 Current User:', storeState.currentUser);
    console.log('🏢 Current Activity:', storeState.currentActivity);
    console.log('⚙️ Activity Settings:', storeState.activitySettings);
    console.log('🎨 Theme:', storeState.theme);
    console.log('📄 Page Title:', storeState.pageTitle);
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <Link 
          to={`/${lang}/`}
          className="flex items-center justify-center text-indigo-600 hover:text-indigo-500 mb-4"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M9.707 14.707a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 1.414L7.414 9H15a1 1 0 110 2H7.414l2.293 2.293a1 1 0 010 1.414z" clipRule="evenodd" />
          </svg>
          Retour à l'accueil
        </Link>
        <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
          Connexion
        </h2>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
              <p className="text-red-600">{error}</p>
            </div>
          )}

          <form className="space-y-6" onSubmit={handleSubmit}>
            <div>
              <label htmlFor="username" className="block text-sm font-medium text-gray-700">
                Nom d'utilisateur
              </label>
              <div className="mt-1">
                <input
                  id="username"
                  name="username"
                  type="text"
                  required
                  value={formData.username}
                  onChange={handleChange}
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Mot de passe
              </label>
              <div className="mt-1">
                <input
                  id="password"
                  name="password"
                  type="password"
                  required
                  value={formData.password}
                  onChange={handleChange}
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                />
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={isLoading}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
              >
                {isLoading ? 'Connexion en cours...' : 'Se connecter'}
              </button>
            </div>
          </form>

          {/* Bouton pour afficher le store */}
          <div className="mt-6 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={handleShowStore}
              className="w-full flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Afficher le Store (Console)
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage; 